/**
 * Custom Hook for Validation State Management
 * Manages error and hint states for both general and sensor configurations
 */
import { useState, useCallback } from 'react';
import { DeviceType } from '@packages/types/device/common/deviceTypes';
import type {
  FieldError,
  ValidationState,
  HintsState,
  ValidationResult,
} from '../types/validation';

export const useValidationState = (deviceType: DeviceType) => {
  const [validationState, setValidationState] = useState<ValidationState>({
    general: {},
    sensors: {},
  });

  const [hintsState, setHintsState] = useState<HintsState>({
    general: {},
    sensors: {},
  });

  /**
   * Update validation state with new results
   */
  const setValidationResult = useCallback((result: ValidationResult) => {
    setValidationState({
      general: result.generalErrors,
      sensors: result.sensorErrors,
    });
    setHintsState({
      general: result.generalHints,
      sensors: result.sensorHints,
    });
  }, []);

  /**
   * Check if there are any validation errors.
   * LITE: 센서별 중첩 구조(Record<number, Record<string, FieldError>>)를 sensorCount까지 확인
   * MINI: 단일 평면 구조(Record<string, FieldError>)를 확인
   */
  const hasErrors = useCallback(
    (sensorCount = 5) => {
      const hasGeneralErrors = Object.values(validationState.general).some(error => error.hasError);

      const sensorErrors = validationState.sensors;
      let hasSensorErrors = false;

      if (deviceType === DeviceType.LITE) {
        const liteErrors = sensorErrors as Record<number, Record<string, FieldError>>;
        for (let i = 0; i < sensorCount; i++) {
          const errors = liteErrors[i];
          if (errors && Object.values(errors).some(error => error.hasError)) {
            hasSensorErrors = true;
            break;
          }
        }
      } else {
        hasSensorErrors = Object.values(sensorErrors as Record<string, FieldError>).some(
          error => error.hasError
        );
      }

      return hasGeneralErrors || hasSensorErrors;
    },
    [validationState, deviceType]
  );

  /**
   * Clear all validation states
   */
  const clearValidation = useCallback(() => {
    setValidationState({
      general: {},
      sensors: {},
    });
    setHintsState({
      general: {},
      sensors: {},
    });
  }, []);

  return {
    validationState,
    hintsState,
    setValidationResult,
    hasErrors,
    clearValidation,
  };
};
